import React, { useState } from 'react';
import { categoryEmoji, formatMoney, paymentTypeLabel } from '../../utils/eventUtils';

// One event: its bills, the payments made so far and the agreed split between members.
function EventDetailView({
  event,
  currentUser,
  getName,
  canManage,
  canContribute,
  canEditItem,
  onBack,
  onEditEvent,
  onDeleteEvent,
  onAddItem,
  onEditItem,
  onChangeTotal,
  onDeleteItem,
  onRecordPayment,
  onDeletePayment,
}) {
  const { summary } = event;
  const [pickingBill, setPickingBill] = useState(false);
  const editableItems = event.items.filter(canEditItem);
  // The event total is the sum of its bills, so changing it means changing one bill or adding one.
  const handleChangeEventTotal = () => {
    if (event.items.length === 0) {
      onAddItem();
    } else if (event.items.length === 1 && editableItems.length === 1) {
      onChangeTotal(editableItems[0]);
    } else {
      setPickingBill(!pickingBill);
    }
  };
  const displayName = (userId) => (userId === currentUser.id ? 'You' : getName(userId));
  const itemName = (itemId) => event.items.find((i) => i.id === itemId)?.name;
  const allSplit = summary.items.every((i) => i.splitComplete);
  // A payment counts in balances once its item is fully split (every item, for whole-event payments).
  const isCounted = (payment) => {
    const itemSummary = summary.items.find((i) => i.itemId === payment.itemId);
    return itemSummary ? itemSummary.splitComplete : allSplit;
  };
  const partText = (part) => {
    const text =
      part.shares.length === 1
        ? `${formatMoney(part.amount)} for ${displayName(part.shares[0].userId)}`
        : `${formatMoney(part.amount)} ${part.splitType === 'EQUAL' ? 'equally' : 'split'} among ${part.shares.length}`;
    return part.name ? `${part.name}: ${text}` : text;
  };
  const paidPercent = Number(summary.total) > 0
    ? Math.min(100, Math.floor((Number(summary.paid) / Number(summary.total)) * 100))
    : 0;

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const d = new Date(dateString);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  return (
    <div className="event-detail">
      <div className="card-box container-large">
        <div className="container-header event-detail-header">
          <div className="event-detail-title">
            <button className="btn btn-secondary btn-sm" onClick={onBack}>
              ← All events
            </button>
            <h3>
              {categoryEmoji(event.category)} {event.name}
            </h3>
            {event.status === 'CLOSED' && <span className="count-badge">Closed</span>}
          </div>
          <div className="event-detail-actions">
            {canContribute && (
              <>
                <button className="btn btn-secondary btn-sm" onClick={onAddItem}>
                  ➕ Add item
                </button>
                <button
                  className="btn btn-primary btn-sm"
                  onClick={() => onRecordPayment('')}
                  disabled={Number(summary.outstanding) <= 0}
                >
                  💸 Record payment
                </button>
              </>
            )}
            {canManage && (
              <>
                <button className="btn btn-secondary btn-sm" onClick={onEditEvent}>
                  ✏️ Edit
                </button>
                <button className="btn btn-danger btn-sm" onClick={onDeleteEvent}>
                  🗑️ Delete
                </button>
              </>
            )}
          </div>
        </div>

        <section className="overview-cards">
          <div className="stat-card">
            <div className="stat-label">Total</div>
            <div className="stat-value">{formatMoney(summary.total)}</div>
            <div className="stat-indicator">across {event.items.length} item(s)</div>
            {(canContribute || editableItems.length > 0) && (
              <button className="btn-tiny stat-card-action" onClick={handleChangeEventTotal}>
                Change total
              </button>
            )}
          </div>
          <div className="stat-card">
            <div className="stat-label">Paid so far</div>
            <div className="stat-value positive">{formatMoney(summary.paid)}</div>
            <div className="stat-indicator">{paidPercent}% of the total</div>
          </div>
          <div className="stat-card">
            <div className="stat-label">Still due</div>
            <div className={`stat-value ${Number(summary.outstanding) > 0 ? 'negative' : 'positive'}`}>
              {formatMoney(summary.outstanding)}
            </div>
            <div className="stat-indicator">
              {event.expectedPayerId ? `${displayName(event.expectedPayerId)} to pay` : 'to the vendor'}
            </div>
          </div>
        </section>

        <div className="event-progress" aria-label={`${paidPercent}% paid`}>
          <div className="event-progress-fill" style={{ width: `${paidPercent}%` }} />
        </div>

        {pickingBill && (
          <div className="event-total-picker">
            <span className="event-form-section-hint">
              The event total is the sum of its bills. Which bill is changing?
            </span>
            <div className="event-total-picker-options">
              {editableItems.map((item) => (
                <button
                  key={item.id}
                  className="btn btn-secondary btn-sm"
                  onClick={() => {
                    setPickingBill(false);
                    onChangeTotal(item);
                  }}
                >
                  {item.name} · {formatMoney(item.totalAmount)}
                </button>
              ))}
              {canContribute && (
                <button
                  className="btn btn-secondary btn-sm"
                  onClick={() => {
                    setPickingBill(false);
                    onAddItem();
                  }}
                >
                  ➕ Add a new bill
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Agreed split: always visible to everyone in the group */}
      <div className="card-box container-large">
        <div className="container-header">
          <h3>🤝 Agreed Split</h3>
          <span className="count-badge">{summary.members.length} people</span>
        </div>
        <div className="event-table">
          <div className="event-table-row event-table-head event-split-grid">
            <span>Member</span>
            <span>Share</span>
            <span>Paid</span>
            <span>Pending to vendor</span>
            <span>Balance now</span>
          </div>
          {summary.members.map((m) => (
            <div key={m.userId} className="event-table-row event-split-grid">
              <span className="main-text" data-label="Member">{displayName(m.userId)}</span>
              <span data-label="Share">{formatMoney(m.share)}</span>
              <span data-label="Paid">{formatMoney(m.paid)}</span>
              <span data-label="Pending to vendor">{formatMoney(m.pending)}</span>
              <span
                data-label="Balance now"
                className={Number(m.net) > 0 ? 'positive' : Number(m.net) < 0 ? 'negative' : ''}
              >
                {Number(m.net) > 0
                  ? `gets back ${formatMoney(m.net)}`
                  : Number(m.net) < 0
                    ? `owes ${formatMoney(Math.abs(m.net))}`
                    : 'settled'}
              </span>
            </div>
          ))}
        </div>
        {Number(summary.unsplit) > 0 && (
          <div className="remaining-amount-banner pending">
            <span>
              <strong>{formatMoney(summary.unsplit)}</strong> is not yet split
              {Number(summary.awaitingSplitPaid) > 0 && (
                <>
                  . <strong>{formatMoney(summary.awaitingSplitPaid)}</strong> already paid is recorded but will
                  only count in balances once the split is complete
                </>
              )}
              .
            </span>
          </div>
        )}
        <p className="event-hint">
          Balances only count money that has actually been paid. The pending amount is owed to the vendor, not
          to each other.
        </p>
      </div>

      <div className="card-box container-large">
        <div className="container-header">
          <h3>🧾 Items</h3>
          <span className="count-badge">{event.items.length} total</span>
        </div>
        {event.items.length === 0 ? (
          <div className="empty-state">
            <p>No items yet. Add the first bill for this event.</p>
          </div>
        ) : (
          <div className="event-table">
            {event.items.map((item) => {
              const itemSummary = summary.items.find((i) => i.itemId === item.id);
              return (
                <div key={item.id} className="event-item-row">
                  <div className="event-item-main">
                    <span className="main-text">{item.name}</span>
                    <span className="sub-text">
                      {formatMoney(itemSummary.paid)} paid of {formatMoney(itemSummary.total)} ·{' '}
                      {Number(itemSummary.outstanding) > 0
                        ? `${formatMoney(itemSummary.outstanding)} due`
                        : 'fully paid'}
                      {item.expectedPayerId && Number(itemSummary.outstanding) > 0
                        ? ` · ${displayName(item.expectedPayerId)} to pay`
                        : ''}
                    </span>
                    {((item.parts || []).length > 1 || (item.parts || []).some((part) => part.name)) && (
                      <span className="sub-text">Split: {item.parts.map(partText).join(' · ')}</span>
                    )}
                    {!itemSummary.splitComplete && (
                      <span className="event-unsplit-text">
                        {formatMoney(itemSummary.unallocated)} not yet split
                      </span>
                    )}
                    {(item.totalChanges || []).map((change) => (
                      <span key={change.id} className="sub-text">
                        Total changed {formatMoney(change.fromAmount)} → {formatMoney(change.toAmount)} by{' '}
                        {displayName(change.changedBy)}
                        {change.note ? ` · ${change.note}` : ''} · {formatDate(change.changedAt)}
                      </span>
                    ))}
                    <div className="event-progress small">
                      <div className="event-progress-fill" style={{ width: `${itemSummary.paidPercent}%` }} />
                    </div>
                  </div>
                  <div className="split-details">
                    {item.shares.map((s) => (
                      <div key={s.userId} className="split-detail-pill">
                        <span>{displayName(s.userId)}</span>
                        <span>{formatMoney(s.amount)}</span>
                      </div>
                    ))}
                  </div>
                  <div className="event-row-actions">
                    {canContribute && Number(itemSummary.outstanding) > 0 && (
                      <button className="btn-tiny" onClick={() => onRecordPayment(item.id)}>
                        Pay
                      </button>
                    )}
                    {canEditItem(item) && (
                      <>
                        <button className="btn btn-secondary btn-sm" onClick={() => onEditItem(item)}>
                          {itemSummary.splitComplete ? 'Edit' : 'Split'}
                        </button>
                        <button className="btn btn-secondary btn-sm" onClick={() => onChangeTotal(item)}>
                          Change total
                        </button>
                        <button className="btn btn-danger btn-sm" onClick={() => onDeleteItem(item)}>
                          Delete
                        </button>
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div className="card-box container-large">
        <div className="container-header">
          <h3>💸 Payments</h3>
          <span className="count-badge">{event.payments.length} total</span>
        </div>
        {event.payments.length === 0 ? (
          <div className="empty-state">
            <p>No payments recorded yet.</p>
          </div>
        ) : (
          <div className="event-table">
            {event.payments.map((payment) => (
              <div key={payment.id} className="event-table-row event-payment-grid">
                <div className="cell-text">
                  <span className="main-text">{payment.label}</span>
                  <span className="sub-text">
                    {paymentTypeLabel(payment.type)} · {itemName(payment.itemId) || 'Whole event'} ·{' '}
                    {formatDate(payment.paidOn)}
                    {!isCounted(payment) && ' · not in balances until the split is complete'}
                  </span>
                </div>
                <span className="payer-badge">{displayName(payment.paidByUserId)}</span>
                <span className="val-bold">{formatMoney(payment.amount)}</span>
                <span className="event-row-actions">
                  {(canManage || payment.recordedBy === currentUser.id) && (
                    <button
                      className="btn-icon-delete"
                      title="Delete payment"
                      aria-label={`Delete payment ${payment.label}`}
                      onClick={() => onDeletePayment(payment)}
                    >
                      🗑️
                    </button>
                  )}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default EventDetailView;
